import { List, ListItem, ListItemText, Typography } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcJoinComponents, MprcRequestErrorReport, MprcSpinnerIndicator } from "components";
import { MprcBoxDrawer } from "components/MprcBoxDrawer";
import { ManagedQuestionVersionListResponse, QuestionClass } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import { isCanceledError, useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditQuestion } from "views/assessment/questionBank/Home/EditQuestion/EditQuestion";

////////////////////////////////////////////////////////////////////////////////

interface IPros {
    questionId: string;
    title?: string;
    onClose: () => void;
}

export const QuestionVersionDrawer = (props: IPros) => {

    const api = useCoreApi();
    const { t } = useTranslation("assessment");

    const viewData = useRef<ManagedQuestionVersionListResponse[] | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const abortControllerRef = useRef<AbortController | undefined>(undefined);
    const [viewError, setViewError] = useState<any>();
    const [dialog, setDialog] = useState<any>();

    const { questionId, title, onClose } = props;

    ///
    const handleClick = (item: ManagedQuestionVersionListResponse) => {
        setDialog(
            <EditQuestion
                questionId={item.questionId}
                version={item.version}
                questionGroupId={item.questionGroupId == null ? undefined : item.questionGroupId}
                questionType={item.questionType}
                forSurvey={item.questionClass == QuestionClass.Survey}
                readonly
                onClose={() => setDialog(null)}
            />
        );
    };

    async function loadData() {
        try {
            setLoading(true);

            abortControllerRef.current = new AbortController();
            var result = await api.v1.questionBank.getQuestionVersions({ questionId }, { signal: abortControllerRef.current.signal } );
            viewData.current = result.data;

            setLoading(false);
        }
        catch (e) {
            if (!isCanceledError(e)) {
                setViewError(e);
                viewData.current = null;
            }
            setLoading(false);
        }
        finally {
            abortControllerRef.current = undefined;
        }
    }

    ///
    const buildSecondLine = (item: ManagedQuestionVersionListResponse) => {
        let c: React.ReactNode[] = [];

        if (item.createdOn == item.modifiedOn)
            c.push(<Typography variant="caption" color="muted.main">{t("QuestionVersionDrawer.ModifiedText", { name: item.modifiedName })}</Typography>);
        else {
            c.push(<Typography variant="caption" color="muted.main">{t("QuestionVersionDrawer.CreatedByText", { name: item.createdName })}</Typography>);
        }

        if (item.applyCount > 0) {
            c.push(<Typography variant="caption" color="muted.main">{t("QuestionVersionDrawer.AppliedText", { count: item.applyCount })}</Typography>);
        } else {
            c.push(<Typography variant="caption" color="muted.main">{t("QuestionVersionDrawer.NoApliedText")}</Typography>);
        }

        return <Typography variant="caption"><MprcJoinComponents components={c} separator={<span>&nbsp;&bull;&nbsp;</span>} /></Typography>;
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT`s
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        loadData();
    }, []);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            { dialog }
            <MprcBoxDrawer
                onClose={() => onClose()}
                title={t("QuestionVersionDrawer.Title")}
                subtitle={title}
                content={
                    <>
                        <MprcRequestErrorReport error={viewError} />
                        {
                            loading
                                ? <MprcSpinnerIndicator />
                                : <>
                                    {
                                        viewData.current && viewData.current.length > 0 &&
                                        <List disablePadding>
                                            {
                                                viewData.current.map((item, index) => (
                                                    <ListItem key={index} disableGutters onClick={() => handleClick(item)} className={AppGlobalClassKeys.clickable}>
                                                        <ListItemText
                                                            disableTypography
                                                            primary={
                                                                <Typography variant="body2" fontWeight="bold" color="Highlight">
                                                                    {Formaters.momentFullDateTime(item.publishedOn ?? item.createdOn)}
                                                                    {
                                                                        item.isCurrent &&
                                                                        <Typography variant="body2" fontWeight="bold" component="span" color="Highlight">
                                                                            <>&nbsp;</>({t("QuestionVersionDrawer.CurrentVersionText")})
                                                                        </Typography>
                                                                    }
                                                                </Typography>
                                                            }
                                                            secondary={buildSecondLine(item)}
                                                        />
                                                    </ListItem>
                                                ))
                                            }
                                        </List>
                                    }
                                </>
                        }
                    </>
                }
            />
        </>

    );
};
